import { CSSProperties, FC } from "react";

import { withActiveAv } from "components/av/AvSubsystemLoader";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { Menu, MenuDivider, MenuItem } from "mds/components/Menu";
import { RoundedButton } from "mds/components/buttons/RoundedButton";
import { ChevronUpIcon, PresentPlayBackgroundIcon, PresentPlayIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectIsClassSessionInPerson, selectIsDesktopDevice } from "store/selectors";
import { selectCurrentBreakoutGroupId } from "store/selectors/breakouts";
import { useAv } from "utils/av";

const LazyScreenshareMenuOption = withActiveAv(() => import("components/av/ScreenshareMenuOption"));

export type PagePresentingMenuProps = {
  styles?: CSSProperties;
  onPractice?: () => void;
  onPresent?: () => void;
  isDisabled?: boolean;
  isPractice?: boolean;
};

export const PagePresentingMenu: FC<PagePresentingMenuProps> = ({
  styles,
  onPractice,
  onPresent,
  isDisabled,
  isPractice,
}) => {
  const isInPersonClassSession = useAppSelector(selectIsClassSessionInPerson);
  const { isInVirtualClass } = useAv();
  const currentBreakoutGroupId = useAppSelector(selectCurrentBreakoutGroupId);
  const isDesktopDevice = useAppSelector(selectIsDesktopDevice);

  if (isPractice) {
    return (
      <RoundedButton className="gap-2" disabled={isDisabled} kind="primary" onClick={onPractice}>
        <PresentPlayIcon />
        {t("page_presenting_menu.practice")}
      </RoundedButton>
    );
  }

  if (isInPersonClassSession) {
    return (
      <RoundedButton className="gap-2" disabled={isDisabled} kind="primary" onClick={onPresent}>
        <PresentPlayIcon />
        {t("page_presenting_menu.present")}
      </RoundedButton>
    );
  }

  const MenuButton = (
    <RoundedButton className="gap-2" disabled={isDisabled} kind="primary">
      <PresentPlayIcon />
      {t("page_presenting_menu.present")}
      <ChevronUpIcon />
    </RoundedButton>
  );

  // If the user is not in desktop and in a breakout group, there will be no available options
  if (currentBreakoutGroupId && !isDesktopDevice) return null;

  return (
    <Menu
      className="dark-menu min-w-[180px]"
      direction="top"
      menuButton={MenuButton}
      menuStyle={styles}
    >
      {!currentBreakoutGroupId && (
        <MenuItem eventName="Presentation Menu - Start" onClick={onPresent}>
          <IconText
            iconStart={<PresentPlayBackgroundIcon className="text-green" />}
            text={t("page_presenting_menu.start_presenting")}
          />
        </MenuItem>
      )}

      {isDesktopDevice && isInVirtualClass && !currentBreakoutGroupId && <MenuDivider />}

      {isDesktopDevice && <LazyScreenshareMenuOption />}
    </Menu>
  );
};
